import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Trophy, RotateCcw, ChevronLeft, Target, Clock, Award, Crown, Star } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import type { GrandQuizSession } from '../lib/types';

interface LeaderboardEntry {
  user_id: string;
  score: number;
  total_questions: number;
  questions_answered: number;
  completion_rate: number;
  score_percentage: number;
  completed_at: string;
  total_response_time_ms: number;
  user_display_name?: string;
  rank?: number;
}

const GrandQuizResultsPage = () => {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const [session, setSession] = useState<GrandQuizSession | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [userEntry, setUserEntry] = useState<LeaderboardEntry | null>(null);
  const [userRank, setUserRank] = useState<number>(0);
  const [isUserInTop5, setIsUserInTop5] = useState(false);
  const [selectedQuestionCount, setSelectedQuestionCount] = useState<number>(20);
  const [selectedNiveau, setSelectedNiveau] = useState<string>('');
  const [selectedMatiere, setSelectedMatiere] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const niveaux = ['6ème', '5ème', '4ème', '3ème', 'Seconde', 'Première', 'Terminale'];
  const questionCounts = [10, 20, 50, 100];

  useEffect(() => {
    const fetchSessionAndLeaderboard = async () => {
      if (!sessionId || !user) return;

      try {
        // Fetch session details
        const { data: sessionData, error: sessionError } = await supabase
          .from('grand_quiz_sessions')
          .select('*')
          .eq('id', sessionId)
          .eq('user_id', user.id)
          .single();

        if (sessionError) throw sessionError;
        if (!sessionData) throw new Error('Session non trouvée');

        setSession(sessionData);
        setSelectedQuestionCount(sessionData.total_questions);
        setSelectedNiveau(sessionData.niveau);
        setSelectedMatiere(sessionData.matiere);

        // Fetch leaderboard
        await fetchLeaderboard(sessionData.total_questions, sessionData.niveau, sessionData.matiere);

      } catch (err) {
        console.error('Error fetching session:', err);
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessionAndLeaderboard();
  }, [sessionId, user]);

  const fetchLeaderboard = async (questionCount: number, niveau: string, matiere: string) => {
    setIsLoadingLeaderboard(true);
    try {
      console.log('🔍 Fetching leaderboard for:', { questionCount, niveau, matiere });

      // Fetch sessions with at least one question answered
      const { data: sessions, error: sessionsError } = await supabase
        .from('grand_quiz_sessions')
        .select('user_id, correct_answers, total_questions, current_question, score_percentage, completion_rate, completed_at, status, total_response_time_ms')
        .eq('total_questions', questionCount)
        .eq('niveau', niveau)
        .eq('matiere', matiere)
        .gt('current_question', 1) // Only sessions that have answered at least one question
        .order('completion_rate', { ascending: false }) // Sort by completion rate first
        .order('correct_answers', { ascending: false }) // Then by absolute score
        .order('total_response_time_ms', { ascending: true }) // Then by response time (faster is better)
        .order('completed_at', { ascending: true }) // Finally by completion time
        .limit(50);

      if (sessionsError) {
        console.error('❌ Sessions error:', sessionsError);
        throw sessionsError;
      }

      console.log('✅ Found sessions:', sessions?.length || 0);

      if (!sessions || sessions.length === 0) {
        console.log('⚠️ No sessions found');
        setLeaderboard([]);
        setUserEntry(null);
        setUserRank(0);
        setIsUserInTop5(false);
        return;
      }

      // Get unique user IDs
      const userIds = [...new Set(sessions.map(session => session.user_id))];
      console.log('👥 Unique user IDs:', userIds.length, userIds);

      // Fetch all profiles
      const { data: allProfiles, error: allProfilesError } = await supabase
        .from('profiles')
        .select('id, first_name, email');

      if (allProfilesError) {
        console.error('❌ Error fetching all profiles:', allProfilesError);
      }

      // Filter profiles for users who have sessions
      let profiles = [];
      try {
        profiles = (allProfiles || []).filter(profile => userIds.includes(profile.id));
        console.log('👤 Found profiles:', profiles.length);
      } catch (profileError) {
        console.warn('⚠️ Profile fetch failed, continuing without profile data:', profileError);
      }

      // Create a map of user_id to display name for quick lookup
      const userDisplayMap = new Map();
      
      // Process profiles if we have them
      profiles.forEach(profile => {
        let displayName = 'Utilisateur anonyme';
        
        if (profile.first_name) {
          displayName = profile.first_name;
        } else if (profile.email) {
          displayName = profile.email.split('@')[0];
        }
        
        // Add "(vous)" for current user
        if (profile.id === user?.id) {
          displayName += ' (vous)';
        }
        
        userDisplayMap.set(profile.id, displayName);
      });

      // For users without profiles, create anonymous entries
      userIds.forEach(userId => {
        if (!userDisplayMap.has(userId)) {
          const displayName = userId === user?.id ? 'Utilisateur anonyme (vous)' : 'Utilisateur anonyme';
          userDisplayMap.set(userId, displayName);
        }
      });

      console.log('🏷️ User display map:', Array.from(userDisplayMap.entries()));

      // Process sessions and group by user (keep best score per user)
      const userBestScores = new Map();
      
      sessions.forEach(session => {
        const questionsAnswered = session.current_question - 1;
        const completionRate = session.completion_rate || 0;
        const scorePercentage = session.score_percentage || 0;
        const responseTime = session.total_response_time_ms || 0;
        const existing = userBestScores.get(session.user_id);
        
        // Determine if this session is better than the existing one
        const isBetter = !existing || 
          completionRate > existing.completion_rate || 
          (completionRate === existing.completion_rate && session.correct_answers > existing.score) ||
          (completionRate === existing.completion_rate && session.correct_answers === existing.score && responseTime < existing.total_response_time_ms);
        
        if (isBetter) {
          userBestScores.set(session.user_id, {
            user_id: session.user_id,
            score: session.correct_answers,
            total_questions: session.total_questions,
            questions_answered: questionsAnswered,
            completion_rate: completionRate,
            score_percentage: scorePercentage,
            total_response_time_ms: responseTime,
            completed_at: session.completed_at || session.updated_at,
            user_display_name: userDisplayMap.get(session.user_id) || 'Utilisateur anonyme'
          });
        }
      });

      console.log('🏆 User best scores:', userBestScores.size, Array.from(userBestScores.entries()));

      // Convert to array and sort with tie-breaking logic
      const leaderboardData: LeaderboardEntry[] = Array.from(userBestScores.values())
        .sort((a, b) => {
          // Primary sort: completion rate (descending)
          if (b.completion_rate !== a.completion_rate) {
            return b.completion_rate - a.completion_rate;
          }
          
          // Secondary sort: absolute score (descending)
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          
          // Tertiary sort: response time (ascending - faster is better)
          if (a.total_response_time_ms !== b.total_response_time_ms) {
            return a.total_response_time_ms - b.total_response_time_ms;
          }
          
          // If everything is equal, they have the same rank
          return 0;
        });

      // Assign ranks with tie handling
      let currentRank = 1;
      for (let i = 0; i < leaderboardData.length; i++) {
        if (i > 0) {
          const current = leaderboardData[i];
          const previous = leaderboardData[i - 1];
          
          // Check if current entry should have the same rank as previous
          const sameCompletionRate = current.completion_rate === previous.completion_rate;
          const sameScore = current.score === previous.score;
          const sameResponseTime = current.total_response_time_ms === previous.total_response_time_ms;
          
          if (sameCompletionRate && sameScore && sameResponseTime) {
            // Same rank as previous
            leaderboardData[i].rank = previous.rank;
          } else {
            // New rank
            currentRank = i + 1;
            leaderboardData[i].rank = currentRank;
          }
        } else {
          // First entry gets rank 1
          leaderboardData[i].rank = 1;
        }
      }

      console.log('🥇 Final leaderboard data:', leaderboardData.length);

      // Find user's entry and rank
      const currentUserEntry = leaderboardData.find(entry => entry.user_id === user?.id);
      const currentUserRank = currentUserEntry?.rank || 0;
      const isInTop5 = currentUserRank <= 5 && currentUserRank > 0;

      console.log('🎯 Current user entry:', currentUserEntry);
      console.log('🏅 Current user rank:', currentUserRank);
      console.log('⭐ Is in top 5:', isInTop5);

      // Set top 5 for display
      const top5 = leaderboardData.slice(0, 5);

      setLeaderboard(top5);
      setUserEntry(currentUserEntry || null);
      setUserRank(currentUserRank);
      setIsUserInTop5(isInTop5);

    } catch (err) {
      console.error('❌ Error fetching leaderboard:', err);
      setError('Erreur lors du chargement du classement');
      // Don't fail completely, just show empty leaderboard
      setLeaderboard([]);
      setUserEntry(null);
      setUserRank(0);
      setIsUserInTop5(false);
    } finally {
      setIsLoadingLeaderboard(false);
    }
  };

  const handleFilterChange = async () => {
    if (selectedQuestionCount && selectedNiveau && selectedMatiere) {
      await fetchLeaderboard(selectedQuestionCount, selectedNiveau, selectedMatiere);
    }
  };

  useEffect(() => {
    if (selectedQuestionCount && selectedNiveau && selectedMatiere && session) {
      handleFilterChange();
    }
  }, [selectedQuestionCount, selectedNiveau, selectedMatiere]);

  const getScoreColor = (completionRate: number) => {
    if (completionRate === 100) return 'text-yellow-600';
    if (completionRate >= 80) return 'text-green-600';
    if (completionRate >= 60) return 'text-blue-600';
    return 'text-gray-600';
  };

  const getScoreBadge = (completionRate: number) => {
    if (completionRate === 100) return '🏆';
    if (completionRate >= 80) return '🥇';
    if (completionRate >= 60) return '🥈';
    return '🥉';
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="h-5 w-5 text-yellow-500" />;
      case 2:
        return <Trophy className="h-5 w-5 text-gray-400" />;
      case 3:
        return <Award className="h-5 w-5 text-amber-600" />;
      default:
        return <Star className="h-5 w-5 text-gray-400" />;
    }
  };

  const formatResponseTime = (timeMs: number) => {
    if (timeMs === 0) return 'N/A';
    const seconds = Math.round(timeMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error || 'Session non trouvée'}</p>
          <Link
            to="/grand-quiz"
            className="mt-4 inline-flex items-center text-sm text-red-600 hover:text-red-500"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Retour à la configuration
          </Link>
        </div>
      </div>
    );
  }

  const questionsAnswered = session.current_question - 1;
  const userScorePercentage = questionsAnswered > 0 ? (session.correct_answers / questionsAnswered) * 100 : 0;
  const userCompletionRate = (session.correct_answers / session.total_questions) * 100;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Results Header */}
        <div className="bg-white shadow sm:rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-purple-600 to-blue-600">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">Résultats du Grand Quiz</h1>
                <p className="text-purple-100">{session.niveau} - {session.matiere}</p>
              </div>
              <Trophy className="h-12 w-12 text-yellow-300" />
            </div>
          </div>

          <div className="px-4 py-5 sm:p-6">
            {/* Special message for first place */}
            {userRank === 1 && (
              <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg p-6 text-center">
                <div className="flex justify-center mb-4">
                  <Crown className="h-16 w-16 text-yellow-500 animate-bounce" />
                </div>
                <h2 className="text-3xl font-bold text-yellow-700 mb-2">
                  🎉 BRAVO ! 🎉
                </h2>
                <p className="text-xl text-yellow-600 font-semibold">
                  Tu es en tête de notre classement !
                </p>
                <p className="text-lg text-yellow-600 mt-2">
                  Félicitations pour cette performance exceptionnelle ! 👑
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className={`text-4xl font-bold mb-2 ${getScoreColor(userCompletionRate)}`}>
                  {userCompletionRate.toFixed(1)}%
                </div>
                <p className="text-gray-600">Taux de bonnes réponses</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">
                  {session.correct_answers}
                </div>
                <p className="text-gray-600">Bonnes réponses</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-2 flex justify-center items-center">
                  {getRankIcon(userRank)}
                  <span className="ml-2">{getScoreBadge(userCompletionRate)}</span>
                </div>
                <p className="text-gray-600">
                  {userRank > 0 ? `${userRank}${userRank === 1 ? 'er' : 'ème'} rang` : 'Non classé'}
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/grand-quiz"
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Nouveau Grand Quiz
              </Link>
              <Link
                to="/mes-fiches"
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Retour aux fiches
              </Link>
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="bg-white shadow sm:rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center">
                <Award className="h-6 w-6 text-gray-400 mr-2" />
                <h2 className="text-xl font-bold text-gray-900">Top 5 du classement</h2>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <select
                  value={selectedQuestionCount}
                  onChange={(e) => setSelectedQuestionCount(Number(e.target.value))}
                  className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  disabled={isLoadingLeaderboard}
                >
                  {questionCounts.map(count => (
                    <option key={count} value={count}>{count} questions</option>
                  ))}
                </select>
                <select
                  value={selectedNiveau}
                  onChange={(e) => setSelectedNiveau(e.target.value)}
                  className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  disabled={isLoadingLeaderboard}
                >
                  {niveaux.map(niveau => (
                    <option key={niveau} value={niveau}>{niveau}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="px-4 py-5 sm:p-6">
            {isLoadingLeaderboard ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Chargement du classement...</p>
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-6">
                <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucun résultat pour cette configuration</p>
                <p className="text-sm text-gray-400 mt-2">
                  Soyez le premier à faire un Grand Quiz avec ces paramètres !
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Mobile-optimized leaderboard */}
                <div className="block sm:hidden">
                  <div className="space-y-3">
                    {leaderboard.map((entry) => (
                      <div 
                        key={`${entry.user_id}-${entry.completed_at}`} 
                        className={`p-4 rounded-lg border ${
                          entry.user_id === user?.id 
                            ? 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-300' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <span className="text-2xl mr-2">{getScoreBadge(entry.completion_rate)}</span>
                            <span className="font-bold text-lg">{entry.rank}</span>
                            {entry.rank === 1 && <span className="ml-2 text-yellow-500">👑</span>}
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${getScoreColor(entry.completion_rate)}`}>
                              {entry.score}
                            </div>
                            <div className="text-xs text-gray-500">bonnes réponses</div>
                          </div>
                        </div>
                        <div className="text-sm">
                          <span className={entry.user_id === user?.id ? 'font-bold text-purple-700' : 'text-gray-700'}>
                            {entry.user_display_name}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Desktop table */}
                <div className="hidden sm:block overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          Rang
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Prénom
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                          Taux de bonnes réponses
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                          Bonnes réponses
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                          Temps total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {leaderboard.map((entry) => (
                        <tr 
                          key={`${entry.user_id}-${entry.completed_at}`} 
                          className={`${
                            entry.user_id === user?.id 
                              ? 'bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-purple-500' 
                              : ''
                          }`}
                        >
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            <div className="flex items-center">
                              {getRankIcon(entry.rank || 0)}
                              <span className="ml-2 mr-2">{getScoreBadge(entry.completion_rate)}</span>
                              <span className="font-bold">{entry.rank}</span>
                              {entry.rank === 1 && <span className="ml-2 text-yellow-500">👑</span>}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className={entry.user_id === user?.id ? 'font-bold text-purple-700' : ''}>
                              {entry.user_display_name}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                            <span className={`font-medium ${getScoreColor(entry.completion_rate)}`}>
                              {entry.completion_rate.toFixed(1)}%
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center font-medium">
                            {entry.score}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                            {formatResponseTime(entry.total_response_time_ms)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* User's position if not in top 5 */}
                {!isUserInTop5 && userEntry && userRank > 5 && (
                  <div className="mt-6">
                    <div className="text-center py-2">
                      <span className="text-gray-400 text-lg">...</span>
                    </div>
                    
                    {/* Mobile version */}
                    <div className="block sm:hidden">
                      <div className="p-4 rounded-lg border bg-gradient-to-r from-purple-50 to-blue-50 border-purple-300">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <span className="text-2xl mr-2">{getScoreBadge(userEntry.completion_rate)}</span>
                            <span className="font-bold text-lg">{userRank}</span>
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${getScoreColor(userEntry.completion_rate)}`}>
                              {userEntry.score}
                            </div>
                            <div className="text-xs text-gray-500">bonnes réponses</div>
                          </div>
                        </div>
                        <div className="text-sm">
                          <span className="font-bold text-purple-700">
                            {userEntry.user_display_name}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Desktop version */}
                    <div className="hidden sm:block overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300">
                        <tbody className="bg-white">
                          <tr className="bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-purple-500">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                              <div className="flex items-center">
                                {getRankIcon(userRank)}
                                <span className="ml-2 mr-2">{getScoreBadge(userEntry.completion_rate)}</span>
                                <span className="font-bold">{userRank}</span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-purple-700 font-bold">
                              {userEntry.user_display_name}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                              <span className={`font-medium ${getScoreColor(userEntry.completion_rate)}`}>
                                {userEntry.completion_rate.toFixed(1)}%
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center font-medium">
                              {userEntry.score}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                              {formatResponseTime(userEntry.total_response_time_ms)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="text-center mt-2">
                      <p className="text-sm text-gray-600">Votre position dans le classement</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrandQuizResultsPage;